// Local, dependency-free replacement for `vue-clickaway` 2.1.0.
//
// Behaviour is replicated verbatim from the installed library source so the
// `v-on-clickaway` directive and the `clickaway` mixin keep working
// identically. This removes the *usage* of the Vue 2-only `vue-clickaway`
// package without changing behaviour.
//
// Differences vs upstream: only the Vue-2.x runtime warning was dropped
// (it required importing Vue and is irrelevant here); the non-function
// guard is preserved.

const HANDLER = '_vue_clickaway_handler';

function unbind(el) {
  document.documentElement.removeEventListener('click', el[HANDLER], false);
  delete el[HANDLER];
}

function bind(el, binding) {
  unbind(el);

  const callback = binding.value;
  if (typeof callback !== 'function') {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `v-${binding.name}="${binding.expression}" expects a function value, got ${callback}`
      );
    }
    return;
  }

  // @NOTE: Vue binds directives in microtasks, while UI events are dispatched
  //        in macrotasks. This causes the listener to be set up before the
  //        "origin" click event arrives at the document root. To work around
  //        that, we ignore events until the end of the "initial" macrotask.
  let initialMacrotaskEnded = false;
  setTimeout(() => {
    initialMacrotaskEnded = true;
  }, 0);

  el[HANDLER] = (ev) => {
    if (initialMacrotaskEnded && !el.contains(ev.target)) {
      return callback(ev);
    }
    return undefined;
  };

  document.documentElement.addEventListener('click', el[HANDLER], false);
}

export const directive = {
  beforeMount: bind,
  updated(el, binding) {
    if (binding.value === binding.oldValue) return;
    bind(el, binding);
  },
  unmounted: unbind,
};

export const mixin = {
  directives: { onClickaway: directive },
};

export default directive;
